/**
 * Frame.io-style asset review modal.
 *
 * Phase A scope (this file):
 *   - Fullscreen modal with a video/image viewer + SVG drawing overlay
 *   - Tools: select / pencil / rectangle / arrow / text / color / undo / clear
 *   - Each comment captures: timestamp (video), drawing layer (SVG shapes), body text
 *   - Comment list down the right rail; click a comment -> seek + show its drawing
 *   - Submit -> fans out in-app notifications to: assigned editor + AD + CC + uploader
 *
 * Phase B+ defer:
 *   - Threaded replies, resolve/unresolve UI, scrubber dots, mentions, mobile touch.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileName } from '@/components/ui/FileName';
import { useAuth } from '@/hooks/use-auth';
import { useIsStaff } from '@/hooks/use-role';
import { useCan } from '@/hooks/use-scopes';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import ReviewThread from '@/components/creative/ReviewThread';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, MousePointer2, Pencil, Square, ArrowUpRight, Type as TextIcon,
  Undo2, Trash2, Send, Play, Pause, MessageSquare, Star,
  Volume2, VolumeX, Volume1, MapPin, Download as DownloadIcon,
  Maximize2, Minimize2, Loader2, ChevronLeft, ChevronRight, Film, Image as ImageIcon } from 'lucide-react';
import { api } from '@/lib/api';
import FileViewer from '@/components/creative/FileViewer';
import { showAs, typeBadge } from '@/lib/file-types';
import { toast } from 'sonner';
import {
  fetchAssetReviews, createReview, requestWebCopy, deleteAsset, type AssetReview, type DrawingShape,
  type MediaAsset,
} from '@/lib/queries';
import PromptDialog from '@/components/ui/PromptDialog';
import VoiceRecorder from '@/components/ui/VoiceRecorder';
import MentionTextarea, { MentionBody } from '@/components/creative/MentionTextarea';
import { cn } from '@/lib/cn';
import { formatBytes, formatEta } from '@/lib/upload-center';
import { useDownloadCenter, useHasDownloadCenter } from '@/lib/download-center';

const COLORS = ['#ef4444', '#f59e0b', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#a855f7', '#ffffff'];

type Tool = 'select' | 'pencil' | 'rect' | 'arrow' | 'text';

interface Props {
  asset: MediaAsset;
  onClose: () => void;
  /** The other files in the same view, in the order they are shown, so the viewer can step through them. */
  siblings?: MediaAsset[];
  onNavigate?: (a: MediaAsset) => void;
  /** opened from a notification: highlight this comment and start at its moment */
  focusReviewId?: string;
  startAt?: number;
  /** a file was deleted from inside the viewer, so the page can drop it from its own lists */
  onDeleted?: (assetId: string) => void;
}

export default function ReviewModal({ asset, onClose, siblings = [], onNavigate, focusReviewId, startAt, onDeleted }: Props) {
  // files deleted from in here leave the strip at once, before the page's list refetches
  const [removed, setRemoved] = useState<Set<string>>(() => new Set());
  const liveSiblings = removed.size ? siblings.filter((a) => !removed.has(a.id)) : siblings;
  const navList = liveSiblings.length > 1 ? liveSiblings : [];
  const navIndex = navList.findIndex((a) => a.id === asset.id);
  const prevAsset = navIndex > 0 ? navList[navIndex - 1] : null;
  const nextAsset = navIndex >= 0 && navIndex < navList.length - 1 ? navList[navIndex + 1] : null;
  const stripRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const isClient = currentUser?.role === 'client';
  const isStaff = useIsStaff();
  const canWrite = useCan('creatives.write');
  // Same rule as the file card: the server's answer when it gave one, else staff who
  // manage the creative or uploaded the file. A clinic login never deletes.
  const mayDelete = (a: MediaAsset) => !isClient
    && (a.can_delete ?? (isStaff && (canWrite || a.uploaded_by === currentUser?.id)));
  // Aziz 2026-09-28: every kind of file opens here. RAW and HEIC photos show the
  // server-made preview (their proxy); XML, text, PDF, audio and the rest go to FileViewer.
  const shown = showAs(asset);
  const isVideo = shown === 'video';
  const [hideResolved, setHideResolved] = useState(false);
  const stillPreviewReady = asset.proxy_status === 'ready' && !!asset.proxy_url;
  const isImage = shown === 'image' || (shown === 'preview-image' && stillPreviewReady);
  // The 1080p proxy (migration 033) streams at once on any line; the master does
  // not, see the notes below. Play the proxy when it is ready, otherwise fall back
  // to the master and the pre-fetch. Download and the edit always use the master.
  const hasProxy = asset.proxy_status === 'ready' && !!asset.proxy_url;
  // Mahmoud 2026-09-28: Safari cannot decode the Sony camera masters (4:2:2 10-bit) and
  // shows black or a strip. Known from the probe (web_playable false) or from the
  // browser's own decode error; either way the viewer is told and offered a web copy.
  const [decodeFailed, setDecodeFailed] = useState(false);
  const [askingCopy, setAskingCopy] = useState(false);
  const masterUnplayable = !!isVideo && !hasProxy && (asset.web_playable === false || decodeFailed);
  const proxyPending = !!isVideo && (asset.proxy_status === 'queued' || asset.proxy_status === 'pending');
  const playable = (hasProxy ? asset.proxy_url : asset.download_url) || '';

  // While a proxy is being made, ask again every 20 s so it is picked up the
  // moment it lands, and count down locally between answers.
  const [tick, setTick] = useState(0);
  const etaFetchedAt = useRef(Date.now());
  useEffect(() => { etaFetchedAt.current = Date.now(); }, [asset.proxy_eta_seconds, asset.proxy_progress]);
  useEffect(() => {
    if (!proxyPending) return;
    const poll = setInterval(() => qc.invalidateQueries({ queryKey: ['assets', asset.creative_id] }), 20000);
    const sec = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(sec); };
  }, [proxyPending, asset.creative_id, qc]);
  void tick;
  const sinceFetch = (Date.now() - etaFetchedAt.current) / 1000;
  const etaLeft = Math.max(5, (asset.proxy_eta_seconds ?? 120) - sinceFetch);
  const etaTotal = Math.max(1, (asset.proxy_eta_seconds ?? 120) / Math.max(0.05, 1 - (asset.proxy_progress ?? 0)));
  const proxyPct = Math.min(97, Math.round(((asset.proxy_progress ?? 0) + sinceFetch / etaTotal) * 100));

  // When the proxy arrives mid-review the <video> src changes and the element
  // resets. Carry the viewer across: same frame, same play state.
  const carryRef = useRef<{ t: number; playing: boolean } | null>(null);
  const lastSrcRef = useRef(playable);
  useEffect(() => {
    if (lastSrcRef.current === playable) return;
    lastSrcRef.current = playable;
    const v = videoRef.current;
    if (!v || !carryRef.current) return;
    const { t, playing } = carryRef.current;
    const restore = () => {
      v.currentTime = t;
      if (playing) v.play().catch(() => {});
    };
    if (v.readyState >= 1) restore(); else v.addEventListener('loadedmetadata', restore, { once: true });
  }, [playable]);

  // Player + canvas refs
  const videoRef    = useRef<HTMLVideoElement | null>(null);
  //: The WHOLE review surface: picture, drawing canvas, scrubber, tools and the
  //: comment rail. Fullscreen goes on this, never on the <video>. A bare video
  //: handed to the Fullscreen API (or to iOS's own player) drops every overlay and
  //: every control, which is the whole reason you are in here: to stop on a frame,
  //: draw on it, and say what is wrong. Expanding must add room, never take tools.
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const [isFull, setIsFull] = useState(false);
  // The key handler binds once with [] deps, so it would read the first render's
  // value for ever. A ref keeps it honest without re-binding on every change.
  const isFullRef = useRef(false);
  isFullRef.current = isFull;

  // ---- Getting the footage ready ------------------------------------------
  // Measured 19 Sep 2026, and the numbers decide the design:
  //
  //  * these raws are 4K 10-bit camera masters at about 222 Mbps
  //  * ONE Cloud Storage object serves at roughly 80-150 Mbps no matter who asks.
  //    A UAE office line got 103, and a gigabit datacentre in Germany opening 16
  //    connections at once still only reached 153. The ceiling is the object, not
  //    the viewer's internet, which is why a 590 Mbps line does not help.
  //  * so a <video> streaming it directly runs at under half real time and stalls
  //
  // The first attempt paused the video and waited for `buffered` to grow. That can
  // never finish: Chrome deliberately stops filling the buffer of a PAUSED video
  // after about five seconds, so the bar crawled and the estimate ran to minutes.
  // Measured: 4.6s buffered, then nothing, for as long as you care to watch.
  //
  // So we fetch the bytes ourselves and play from memory. fetch() is not subject to
  // the media element's read-ahead policy, the progress is real bytes rather than an
  // inference, and once it is in hand playback is perfect.
  // How much a tab can hold is the device's decision, not ours. Measured 19 Sep:
  // an iPhone fetched a 1.2 GB master to 99% at 25 MB/s and then iOS killed the
  // tab the instant the Blob was built, which the viewer saw as the page reloading
  // and the progress vanishing. A Safari tab gets well under a gigabyte; Chrome
  // spools big Blobs to disk and copes with more. `deviceMemory` is Chrome-only, so
  // its absence is itself the signal for a conservative cap.
  const PRELOAD_MIN_BYTES = 40_000_000;     // below this it streams fine, do not wait
  const PRELOAD_MAX_BYTES = (() => {
    const gb = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
    const touch = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
    if (touch || !gb) return 250_000_000;              // phones, tablets, Safari
    if (gb <= 4) return 500_000_000;
    return 1_200_000_000;                              // a real desktop on Chrome
  })();
  const assetBytes = hasProxy ? (asset.proxy_bytes ?? 0) : (asset.size_bytes ?? 0);
  const tooBigToHold = !!isVideo && !hasProxy && assetBytes > PRELOAD_MAX_BYTES;
  const shouldPreload = !!isVideo && !hasProxy
    && assetBytes >= PRELOAD_MIN_BYTES
    && assetBytes <= PRELOAD_MAX_BYTES;

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [preloading, setPreloading] = useState(false);
  const [gotBytes, setGotBytes] = useState(0);
  const [preloadBps, setPreloadBps] = useState<number | null>(null);
  const [preloadFailed, setPreloadFailed] = useState(false);
  //: the viewer chose to watch it stream instead of waiting
  const [streamAnyway, setStreamAnyway] = useState(false);
  //: nothing is fetched until someone actually asks to watch. Opening a clip to
  //: read its name should not pull 800 MB of billed egress out of the bucket.
  const [asked, setAsked] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const preloadPct = assetBytes > 0 ? Math.min(99, Math.round((gotBytes / assetBytes) * 100)) : 0;
  const preloadEta = preloadBps && preloadBps > 0 && assetBytes > gotBytes
    ? formatEta(Math.round((assetBytes - gotBytes) / preloadBps))
    : '';
  const imageRef    = useRef<HTMLImageElement | null>(null);
  const stageRef    = useRef<HTMLDivElement | null>(null);   // the drawing-coordinates container
  const svgRef      = useRef<SVGSVGElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Player state
  const [playing, setPlaying]         = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  //: the <video> as it actually mounts. The player effect used to read videoRef
  //: during an effect keyed on the source, so an element that mounted later got no
  //: listeners at all and the clock stayed at zero, putting every mark on frame 0.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    setVideoEl((cur) => (cur === el ? cur : el));
  }, []);
  const [duration, setDuration]       = useState(0);
  const [volume, setVolumeState]      = useState(1);   // 0..1
  const [muted, setMutedState]        = useState(false);

  // Tools state
  const [tool,  setTool]  = useState<Tool>('pencil');
  const [color, setColor] = useState('#ef4444');
  //: text marks carry their own size, so a label on a wide shot can be read on a phone
  const [textSize, setTextSize] = useState(28);
  const [draftShapes, setDraftShapes] = useState<DrawingShape[]>([]);
  const [drawing, setDrawing]         = useState<{ start: { x: number; y: number }; current: { x: number; y: number }; pathPoints?: { x: number; y: number }[] } | null>(null);

  // Composer state
  const [body, setBody]       = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  // The frame a mark belongs to. Anchored by the FIRST shape drawn; until then it
  // follows the playhead, so scrubbing to a new place and marking again lands
  // the mark there (the old focus-pins-forever rule stacked every mark on one time).
  const [pinTime, setPinTime] = useState<number | null>(null);
  // Checkpoints: the marks drawn on other frames of the same draft. A mark belongs to one
  // frame; drawing on a new frame keeps the earlier marks with theirs instead of hiding them.
  const [stash, setStash] = useState<{ time: number; shapes: DrawingShape[] }[]>([]);
  const [voice, setVoice] = useState<{ path: string; url: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Active comment showing its drawing read-only
  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);
  const pendingSeekRef = useRef<number | null>(null);

  // For the text tool: where the user clicked is captured here, then the in-app
  // prompt opens. Once they confirm a string, the text shape is appended.
  const [textPrompt, setTextPrompt] = useState<{ x: number; y: number } | null>(null);

  // Scrubber-hover preview state. Position is the cursor's % across the scrubber;
  // pickedTime is the corresponding time-in-video. We pull the thumbnail cell from
  // the asset's preview sprite via CSS background-position.
  const [scrubHover, setScrubHover] = useState<{ pct: number; time: number } | null>(null);
  const scrubberRef = useRef<HTMLDivElement | null>(null);

  // Export-markers menu (Premiere XMP / DaVinci EDL / CSV)
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  async function exportMarkers(fmt: 'xmp' | 'edl' | 'csv') {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      // Authenticated request - axios attaches the JWT. Read the file as a blob
      // so we can trigger a clean browser download with the suggested filename.
      const r = await api.get(`/assets/${asset.id}/reviews/export`, {
        params: { fmt },
        responseType: 'blob',
      });
      const cd = (r.headers['content-disposition'] as string) || '';
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m?.[1] || `${asset.filename}.markers.${fmt}`;
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${filename}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Export failed';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  }

  // ------------- download + delete, from the player itself (Aziz 2026-10-24: the
  // team asked to stop leaving the review to find a clip by its number).
  //: the file being saved and how far it got; one at a time
  const [saving, setSaving] = useState<{ id: string; pct: number | null } | null>(null);
  const [pendingDel, setPendingDel] = useState<MediaAsset | null>(null);
  // The key handler binds once, so it reads these through refs.
  const confirmOpenRef = useRef(false);
  confirmOpenRef.current = !!pendingDel;

  /** Save the original (the master, never the proxy) under its real name.
   *  Up to what this device can hold it is fetched and saved under `filename`, with a
   *  percentage in the toast, the same as the file card. Anything bigger goes to the
   *  browser's own download (signed with Content-Disposition), which streams to disk
   *  and shows its own progress: building a Blob of a 1 GB master is what killed the
   *  iPhone tab on 19 Sep. */
  // Staff downloads go through the Download Center, the same as the file list (Aziz
  // 2026-10-26): one queue, pause and resume, the chosen folder, the dock. The portal has
  // no Download Center, so there the player keeps saving the file itself (below).
  const downloads = useDownloadCenter();
  const hasCenter = useHasDownloadCenter();
  const liveJob = (id: string) => downloads.jobs.find((j) => j.assetId === id && (j.state === 'queued' || j.state === 'downloading' || j.state === 'paused'));
  async function downloadOne(a: MediaAsset) {
    if (hasCenter) {
      const url = a.attachment_url || a.download_url;
      if (!url) { toast.error('This file has no download link'); return; }
      if (liveJob(a.id)) { toast('Already in the Download Center'); return; }
      // the folder picker must open from the click itself
      const dir = downloads.canPickFolder ? await downloads.pickFolder() : null;
      downloads.enqueue([{ assetId: a.id, creativeId: a.creative_id, kind: a.kind, category: a.category ?? undefined,
        name: a.download_name || a.filename || 'download', size: a.size_bytes ?? 0, url }], dir);
      toast.success(`${a.filename || 'File'} queued in the Download Center`);
      return;
    }
    if (saving) return;
    const name = a.filename || 'download';
    const url = a.download_url;
    const big = (a.size_bytes ?? 0) > PRELOAD_MAX_BYTES;
    if ((big || !url) && a.attachment_url) {
      clickLink(a.attachment_url);
      toast.success(`Downloading ${name}`, { description: 'The browser shows the progress in its downloads.' });
      return;
    }
    if (!url) { toast.error('This file has no download link'); return; }
    const tid = toast.loading(`Downloading ${name}`);
    setSaving({ id: a.id, pct: 0 });
    try {
      const res = await fetch(url);
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const total = Number(res.headers.get('content-length')) || a.size_bytes || 0;
      // one branch is counted, the other becomes the Blob (no second copy in memory)
      const [count, keep] = res.body.tee();
      const blobPromise = new Response(keep, { headers: { 'Content-Type': a.content_type || 'application/octet-stream' } }).blob();
      const reader = count.getReader();
      let got = 0; let shownPct = -1;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (!value) continue;
        got += value.byteLength;
        const pct = total ? Math.min(99, Math.round((got / total) * 100)) : null;
        if (pct !== null && pct !== shownPct) {
          shownPct = pct;
          setSaving({ id: a.id, pct });
          toast.loading(`Downloading ${name} - ${pct}%`, { id: tid });
        }
      }
      const blob = await blobPromise;
      const blobUrl = URL.createObjectURL(blob);
      clickLink(blobUrl, name);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success(`Downloaded ${name}`, { id: tid, description: formatBytes(blob.size) });
    } catch {
      if (a.attachment_url) {
        clickLink(a.attachment_url);
        toast.success(`Downloading ${name}`, { id: tid, description: 'Handed to the browser download.' });
      } else {
        toast.error(`Could not download ${name}`, { id: tid });
      }
    } finally {
      setSaving(null);
    }
  }

  /** Delete from inside the player, then carry on with the next clip (or the one
   *  before when it was the last), and close only when nothing is left. */
  async function confirmDelete() {
    const a = pendingDel;
    if (!a) return;
    try {
      await deleteAsset(a.id);
    } catch {
      // the API layer already showed the server's reason (not yours, raws approved...)
      setPendingDel(null);
      return;
    }
    setPendingDel(null);
    toast.success(`Deleted ${a.filename}`);
    qc.invalidateQueries({ queryKey: ['assets', a.creative_id] });
    qc.invalidateQueries({ queryKey: ['creatives'] });
    onDeleted?.(a.id);
    const i = navList.findIndex((x) => x.id === a.id);
    setRemoved((cur) => new Set(cur).add(a.id));
    if (a.id !== asset.id) return;
    const to = i >= 0 ? (navList[i + 1] ?? navList[i - 1] ?? null) : null;
    if (to && onNavigate) onNavigate(to); else onClose();
  }
  const downloadRef = useRef(downloadOne);
  downloadRef.current = downloadOne;
  const canSave = !!(asset.download_url || asset.attachment_url);
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;
  const currentRef = useRef(asset);
  currentRef.current = asset;

  function onScrubberHover(e: React.PointerEvent<HTMLDivElement>) {
    const el = scrubberRef.current;
    if (!el || duration <= 0) return;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setScrubHover({ pct, time: pct * duration });
  }

  // Reviews list
  const reviews = useQuery({
    queryKey: ['asset-reviews', asset.id],
    queryFn: () => fetchAssetReviews(asset.id, true),
  });

  // ------------- player events
  useEffect(() => {
    const v = videoEl;
    if (!v) return;
    const onTime = () => { setCurrentTime(v.currentTime); carryRef.current = { t: v.currentTime, playing: !v.paused }; };
    const onLoad = () => {
      setDuration(v.duration || 0);
      const p = pendingSeekRef.current;
      if (p != null) { pendingSeekRef.current = null; try { v.currentTime = Math.min(v.duration || p, p); } catch { /* */ } }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener('timeupdate', onTime);
    // a scrub while paused fires seeked, not timeupdate
    v.addEventListener('seeked', onTime);
    v.addEventListener('loadedmetadata', onLoad);
    // 3 = could not decode, 4 = format not supported: the master will not play here
    const onError = () => { const c = v.error?.code; if (c === 3 || c === 4) setDecodeFailed(true); };
    setDecodeFailed(false);
    v.addEventListener('error', onError);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onLoad);
      v.removeEventListener('error', onError);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('seeked', onTime);
    };
  }, [videoEl, playable]);

  // Keyboard shortcuts: ESC closes, space toggles play. BUT skip when the user is
  // typing into an input/textarea (otherwise space eats their text and Esc trashes
  // their draft). contentEditable check covers any future rich-text composer.
  useEffect(() => {
    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      const tag = t.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable;
    }
    function onKey(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      // the delete confirm owns Esc and Enter while it is open
      if (confirmOpenRef.current) return;
      if (e.key === 'Escape') {
        if (isFullRef.current) return;   // the browser is leaving fullscreen; keep the review open
        onClose();
        return;
      }
      if (e.key === '[' && prevAsset && onNavigate) { e.preventDefault(); onNavigate(prevAsset); return; }
      if (e.key === ']' && nextAsset && onNavigate) { e.preventDefault(); onNavigate(nextAsset); return; }
      // D saves the original; free in this player (tools are V/P/R/A/T, sizes are buttons)
      if ((e.key === 'd' || e.key === 'D') && !e.ctrlKey && !e.metaKey && !e.altKey && canSaveRef.current) {
        e.preventDefault(); void downloadRef.current(currentRef.current); return;
      }
      if (!isVideo) return;
      const k = e.key.toLowerCase();
      // J/K/L are the de-facto editor shortcuts (Premiere, FCP, YouTube, Frame.io)
      if (k === ' ' || k === 'k')   { e.preventDefault(); togglePlay(); return; }
      if (k === 'f')                { e.preventDefault(); void toggleFullscreen(); return; }
      if (k === 'j')                { e.preventDefault(); seekBy(e.shiftKey ? -1 / 30 : -5); return; }
      if (k === 'l')                { e.preventDefault(); seekBy(e.shiftKey ?  1 / 30 :  5); return; }
      if (k === 'arrowleft')        { e.preventDefault(); seekBy(e.shiftKey ? -1 / 30 : -1); return; }
      if (k === 'arrowright')       { e.preventDefault(); seekBy(e.shiftKey ?  1 / 30 :  1); return; }
      if (k === 'm')                { e.preventDefault(); toggleMute(); return; }
      // Tool hotkeys (single-letter, common in design tools)
      if (k === 'v') setTool('select');
      if (k === 'p') setTool('pencil');
      if (k === 'r') setTool('rect');
      if (k === 'a') setTool('arrow');
      if (k === 't') setTool('text');
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chrome, Firefox and Edge use the standard API; Safari on the desktop still
  // needs the webkit spelling. iPhone Safari has no element fullscreen at all, so
  // the button hides there rather than lying about what it will do.
  const canFullscreen = typeof document !== 'undefined'
    && (document.fullscreenEnabled
        || !!(document as unknown as { webkitFullscreenEnabled?: boolean }).webkitFullscreenEnabled);

  useEffect(() => {
    const on = () => setIsFull(!!(document.fullscreenElement
      || (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement));
    document.addEventListener('fullscreenchange', on);
    document.addEventListener('webkitfullscreenchange', on);
    return () => {
      document.removeEventListener('fullscreenchange', on);
      document.removeEventListener('webkitfullscreenchange', on);
    };
  }, []);

  async function toggleFullscreen() {
    const el = workspaceRef.current as (HTMLDivElement & {
      webkitRequestFullscreen?: () => Promise<void> }) | null;
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void>; webkitFullscreenElement?: Element };
    try {
      if (document.fullscreenElement || doc.webkitFullscreenElement) {
        await (document.exitFullscreen ? document.exitFullscreen() : doc.webkitExitFullscreen?.());
      } else if (el) {
        await (el.requestFullscreen ? el.requestFullscreen() : el.webkitRequestFullscreen?.());
      }
    } catch {
      /* the browser refused; the player keeps working inline, which is the point */
    }
  }

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (!v.paused) { v.pause(); return; }
    if (shouldPreload && !blobUrl && !streamAnyway) { setAsked(true); return; }
    v.play().catch(() => {/* */});
  }

  /** Stop waiting and watch it stream, stalls and all. The viewer's call. */
  function playStreaming() {
    abortRef.current?.abort();
    setPreloading(false);
    setStreamAnyway(true);
    // next tick, once the element is back on the streaming URL
    window.setTimeout(() => videoRef.current?.play().catch(() => {/* */}), 50);
  }

  // The file is in hand: start it, since pressing play is what asked for it.
  useEffect(() => {
    if (!blobUrl) return;
    const v = videoRef.current;
    if (!v) return;
    const go = () => v.play().catch(() => {/* */});
    v.addEventListener('loadeddata', go, { once: true });
    return () => v.removeEventListener('loadeddata', go);
  }, [blobUrl]);

  // Pull the file ourselves, then play it from memory.
  //
  // Deliberately NOT the media element's own buffering: Chrome stops filling a
  // paused video's buffer after a few seconds, so waiting on `buffered` never
  // finishes. fetch() has no such policy and reports real bytes.
  useEffect(() => {
    if (!asked || !shouldPreload || streamAnyway || blobUrl || !playable) return;
    let cancelled = false;
    const ac = new AbortController();
    abortRef.current = ac;
    setPreloading(true);
    setGotBytes(0);
    setPreloadFailed(false);

    (async () => {
      try {
        const res = await fetch(playable, { signal: ac.signal, cache: 'no-store' });
        if (!res.ok || !res.body) throw new Error(String(res.status));
        const total = Number(res.headers.get('content-length')) || assetBytes;
        // Two branches of the same stream: one is counted for progress and thrown
        // away, the other becomes the Blob. The browser builds that Blob itself,
        // spooling to disk where it can, so there is never a second copy sitting in
        // a JavaScript array and no doubling of memory at the end. The first
        // version kept every chunk in an array AND then built the Blob from it,
        // which is what tipped the phone over.
        const [count, keep] = res.body.tee();
        const blobPromise = new Response(keep, {
          headers: { 'Content-Type': asset.content_type || 'video/mp4' },
        }).blob();
        const reader = count.getReader();
        let got = 0;
        let lastAt = performance.now();
        let lastGot = 0;
        let bps: number | null = null;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          if (!value) continue;
          got += value.byteLength;
          const now = performance.now();
          if (now - lastAt >= 500) {
            const sample = ((got - lastGot) * 1000) / (now - lastAt);
            bps = bps === null ? sample : bps * 0.7 + sample * 0.3;
            lastAt = now; lastGot = got;
            if (!cancelled) { setGotBytes(got); setPreloadBps(bps); }
          }
        }
        const blob = await blobPromise;
        if (cancelled) return;
        setGotBytes(total || got);
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setPreloading(false);
      } catch (e) {
        if (cancelled || (e as Error)?.name === 'AbortError') return;
        // CORS, a dropped connection, anything: fall back to streaming rather
        // than leaving someone in front of a spinner.
        setPreloadFailed(true);
        setPreloading(false);
        setStreamAnyway(true);
      }
    })();

    return () => { cancelled = true; ac.abort(); };
  }, [asked, shouldPreload, streamAnyway, blobUrl, playable, assetBytes, asset.content_type]);

  // A blob URL is a live handle into memory; letting it leak keeps the whole file.
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  function toggleMute() {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMutedState(next);
    // If unmuting from 0 volume, bump to a sensible default
    if (!next && v.volume === 0) {
      v.volume = 0.7;
      setVolumeState(0.7);
    }
  }
  function setVolume(v01: number) {
    const v = videoRef.current;
    if (!v) return;
    const clamped = Math.max(0, Math.min(1, v01));
    v.volume = clamped;
    v.muted = clamped === 0;
    setVolumeState(clamped);
    setMutedState(clamped === 0);
  }

  // A seek before the clip has its metadata (a master still preloading) used to be clamped to
  // 0, so clicking a review pinned at 11 s showed nothing. Clamp only to a known duration,
  // move the playhead state at once so the review's marks appear, and replay the seek when
  // the metadata arrives.
  function seekTo(sec: number) {
    const v = videoRef.current;
    if (!v) return;
    const target = Math.max(0, duration > 0 ? Math.min(duration, sec) : sec);
    try { v.currentTime = target; } catch { /* */ }
    if (v.readyState === 0) pendingSeekRef.current = target;
    setCurrentTime(target);
    v.pause();
  }
  // Opened from a notification: highlight that comment (a reply opens its thread), start at
  // its moment, and bring it into view in the rail. Once, when the comments have loaded.
  const focusedRef = useRef(false);
  useEffect(() => {
    if (focusedRef.current || !reviews.data || (!focusReviewId && startAt == null)) return;
    focusedRef.current = true;
    const r = (reviews.data ?? []).find((x) => x.id === focusReviewId);
    const parentId = r ? (r.parent_id ?? r.id) : null;
    const parent = parentId ? (reviews.data ?? []).find((x) => x.id === parentId) : null;
    const at = parent?.timestamp_sec ?? r?.timestamp_sec ?? startAt;
    if (parentId) setActiveReviewId(parentId);
    if (isVideo && at != null) seekTo(Number(at));
    if (parentId) window.setTimeout(() => document.querySelector(`[data-review-id="${parentId}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 400);
  }, [reviews.data]);   // eslint-disable-line react-hooks/exhaustive-deps

  function seekBy(deltaSec: number) {
    const v = videoRef.current;
    if (!v) return;
    const target = Math.max(0, duration > 0 ? Math.min(duration, v.currentTime + deltaSec) : v.currentTime + deltaSec);
    try { v.currentTime = target; } catch { /* */ }
    setCurrentTime(target);
  }

  // ------------- coordinate helpers (always in 0..1 normalized to the stage size)
  function svgToNorm(e: React.MouseEvent<SVGSVGElement> | React.PointerEvent<SVGSVGElement>): { x: number; y: number } {
    const svg = svgRef.current!;
    const rect = svg.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top)  / rect.height,
    };
  }

  // ------------- drawing handlers
  function onSvgPointerDown(e: React.PointerEvent<SVGSVGElement>) {
    if (tool === 'select') return;
    // A pinch to zoom is a second finger landing on the same surface. Without this
    // the second finger was read as a new mark, so the picture could not be zoomed
    // and every attempt left a stray shape or opened the text box.
    if (e.pointerType === 'touch' && !e.isPrimary) return;
    // Starting a new mark while viewing an existing review -> auto-exit view mode
    if (activeReviewId) setActiveReviewId(null);
    if (isVideo && videoRef.current && !videoRef.current.paused) videoRef.current.pause();
    if (isVideo) {
      if (pinTime === null) {
        setPinTime(currentTime);   // first shape anchors the frame
      } else if (Math.abs(currentTime - pinTime) > TIMESTAMP_TOLERANCE) {
        // another frame: what was drawn so far stays with its frame as a checkpoint, and this
        // frame gets its own marks (picking up any it already had)
        const back = stash.find((m) => Math.abs(m.time - currentTime) <= TIMESTAMP_TOLERANCE);
        const keep = draftShapes.length ? [{ time: pinTime, shapes: draftShapes }] : [];
        setStash((cur) => [...cur.filter((m) => m !== back), ...keep]);
        setDraftShapes(back ? back.shapes : []);
        setPinTime(currentTime);
      }
    }
    const start = svgToNorm(e);
    if (tool === 'text') {
      // Defer text capture to the in-app PromptDialog (no native window.prompt)
      setTextPrompt(start);
      return;
    }
    setDrawing({
      start,
      current: start,
      pathPoints: tool === 'pencil' ? [start] : undefined,
    });
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }

  function onSvgPointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (!drawing) return;
    const cur = svgToNorm(e);
    if (tool === 'pencil') {
      setDrawing({ ...drawing, current: cur, pathPoints: [...(drawing.pathPoints ?? []), cur] });
    } else {
      setDrawing({ ...drawing, current: cur });
    }
  }

  function onSvgPointerUp() {
    if (!drawing) return;
    const { start, current, pathPoints } = drawing;
    let shape: DrawingShape | null = null;
    if (tool === 'pencil' && pathPoints && pathPoints.length > 1) {
      const d = `M ${pathPoints[0].x.toFixed(4)} ${pathPoints[0].y.toFixed(4)} ` +
                pathPoints.slice(1).map(p => `L ${p.x.toFixed(4)} ${p.y.toFixed(4)}`).join(' ');
      shape = { type: 'path', d, color, stroke: 0.006 };
    } else if (tool === 'rect') {
      const x = Math.min(start.x, current.x);
      const y = Math.min(start.y, current.y);
      const w = Math.abs(current.x - start.x);
      const h = Math.abs(current.y - start.y);
      if (w > 0.005 || h > 0.005) shape = { type: 'rect', x, y, w, h, color, stroke: 0.006 };
    } else if (tool === 'arrow') {
      const dx = current.x - start.x;
      const dy = current.y - start.y;
      if (Math.hypot(dx, dy) > 0.01) shape = { type: 'arrow', x1: start.x, y1: start.y, x2: current.x, y2: current.y, color, stroke: 0.006 };
    }
    setDrawing(null);
    if (shape) setDraftShapes((cur) => [...cur, shape!]);
  }

  /** Take back the last mark, whichever frame it landed on. It used to stop at the
   *  frame in hand, so after moving the playhead there was nothing left to undo. */
  function undo() {
    if (draftShapes.length) { setDraftShapes((cur) => cur.slice(0, -1)); return; }
    setStash((cur) => {
      if (!cur.length) return cur;
      const last = cur[cur.length - 1];
      const rest = last.shapes.slice(0, -1);
      return rest.length ? [...cur.slice(0, -1), { ...last, shapes: rest }] : cur.slice(0, -1);
    });
  }

  /** Every mark made so far, newest last, each with the frame it belongs to. The list
   *  is what makes a single mark removable: before this, the only way to lose one was
   *  Clear, which took all of them. */
  const allMarks = useMemo(() => {
    const out: { time: number | null; index: number; shape: DrawingShape; frame: number }[] = [];
    stash.forEach((m, frame) => m.shapes.forEach((shape, index) => out.push({ time: m.time, index, shape, frame })));
    draftShapes.forEach((shape, index) => out.push({ time: pinTime, index, shape, frame: -1 }));
    return out;
  }, [stash, draftShapes, pinTime]);

  function removeMark(frame: number, index: number) {
    if (frame === -1) { setDraftShapes((cur) => cur.filter((_, i) => i !== index)); return; }
    setStash((cur) => {
      const next = cur.map((m, f) => (f === frame ? { ...m, shapes: m.shapes.filter((_, i) => i !== index) } : m));
      return next.filter((m) => m.shapes.length > 0);
    });
  }

  function clearDrawing() { setDraftShapes([]); setStash([]); setPinTime(null); }

  // Full composer reset: clear text, drawing, pin, and any focused review.
  // Called after a successful post and when the user clicks "+ New".
  function resetComposer() {
    setBody('');
    setMentions([]);
    setDraftShapes([]);
    setStash([]);
    setPinTime(null);
    setVoice(null);
    setActiveReviewId(null);
  }
  function clearDraft() { setDraftShapes([]); setStash([]); setPinTime(null); }

  // ------------- submit
  async function handlePost() {
    if (!body.trim() && !voice) {
      toast.error('Add a comment or a voice note before posting');
      return;
    }
    setSubmitting(true);
    try {
      // every frame with marks becomes its own review (a checkpoint), same words on each;
      // the voice note and the pings ride on the first so nobody is pinged twice
      const frames = isVideo ? [...stash, ...(pinTime !== null && draftShapes.length ? [{ time: pinTime, shapes: draftShapes }] : [])]
        .sort((a, b) => a.time - b.time) : [];
      if (frames.length > 1) {
        for (const [i, f] of frames.entries()) {
          await createReview(asset.id, {
            body: body.trim(),
            audio_path: i === 0 ? (voice?.path ?? null) : null,
            timestamp_sec: f.time,
            drawing: f.shapes,
            mentioned_user_ids: i === 0 ? mentions : [],
          });
        }
      } else {
        await createReview(asset.id, {
          body: body.trim(),
          audio_path: voice?.path ?? null,
          timestamp_sec: isVideo ? (pinTime ?? currentTime) : null,
          drawing: draftShapes.length ? draftShapes : null,
          mentioned_user_ids: mentions,
        });
      }
      toast.success(isClient
        ? 'Feedback sent to the team'
        : frames.length > 1
          ? `${frames.length} marks posted, one per frame - team notified`
          : mentions.length
            ? `Review posted - ${mentions.length} mention${mentions.length === 1 ? '' : 's'} + team notified`
            : 'Review posted - editor + AD + CC + uploader notified');
      resetComposer();
      qc.invalidateQueries({ queryKey: ['asset-reviews', asset.id] });
      qc.invalidateQueries({ queryKey: ['creative-reviews', asset.creative_id] });
      qc.invalidateQueries({ queryKey: ['unread-count'] });
      // Refocus textarea so the user can immediately add the next comment
      setTimeout(() => textareaRef.current?.focus(), 50);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Post failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // ------------- thread grouping
  // Top-level reviews (parent_id = null) + their replies, ordered by created_at asc.
  // Resolved threads can be filtered out via the toggle.
  const threads = useMemo(() => {
    const list = reviews.data ?? [];
    const repliesByParent = new Map<string, AssetReview[]>();
    const parents: AssetReview[] = [];
    for (const r of list) {
      if (r.parent_id) {
        const arr = repliesByParent.get(r.parent_id) ?? [];
        arr.push(r);
        repliesByParent.set(r.parent_id, arr);
      } else {
        parents.push(r);
      }
    }
    parents.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    for (const arr of repliesByParent.values()) {
      arr.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    }
    return parents.map(p => ({ parent: p, replies: repliesByParent.get(p.id) ?? [] }));
  }, [reviews.data]);

  const visibleThreads = useMemo(
    () => hideResolved ? threads.filter(t => t.parent.status !== 'resolved') : threads,
    [threads, hideResolved],
  );
  const openCount = threads.filter(t => t.parent.status !== 'resolved').length;
  const resolvedCount = threads.length - openCount;

  // ------------- shapes to SVG (Frame.io-style: marks appear only when playhead is at their timestamp)
  // Tolerance window in seconds. Wide enough that scrubbing/playback briefly reveals the mark
  // when the time passes, narrow enough that marks don't visually leak between unrelated frames.
  const TIMESTAMP_TOLERANCE = 0.4;
  function withinTime(target: number | null | undefined): boolean {
    if (!isVideo) return true;            // images: no time concept, always show
    if (target == null) return true;      // unpinned (rare): always show
    return Math.abs(currentTime - target) <= TIMESTAMP_TOLERANCE;
  }

  const activeReview = (reviews.data ?? []).find(r => r.id === activeReviewId) ?? null;

  // Build the final shape list:
  //   1. If a review is "actively focused" (clicked in rail) -> show only its marks at its frame
  //   2. Else -> show ALL reviews whose pinned frame matches the current time, PLUS
  //              the in-progress composer draft (if the playhead is still at the pin frame).
  let shapesShown: DrawingShape[] = [];

  if (activeReview) {
    if (withinTime(activeReview.timestamp_sec)) shapesShown = activeReview.drawing ?? [];
  } else {
    // Other reviews popping in/out as the playhead crosses their timestamps
    for (const r of reviews.data ?? []) {
      if (r.drawing?.length && withinTime(r.timestamp_sec)) {
        shapesShown.push(...r.drawing);
      }
    }
    // Live composer (drafts + currently-being-dragged shape)
    for (const m of stash) {
      if (withinTime(m.time)) shapesShown.push(...m.shapes);
    }
    if (withinTime(pinTime)) {
      shapesShown.push(...draftShapes);
      if (drawing) {
        if (tool === 'pencil') {
          shapesShown.push({ type: 'path', d: pathFromPoints(drawing.pathPoints ?? []), color, stroke: 0.006 });
        } else {
          const eph = ephemeralShape(drawing, tool, color);
          if (eph) shapesShown.push(eph);
        }
      }
    }
  }

  return (
    <div ref={workspaceRef} className="always-dark fixed inset-0 z-[400] bg-black/95 flex flex-col" role="dialog" aria-modal="true">
      {/* Top bar */}
      <header className="h-14 border-b border-slate-800 bg-bg-card/80 backdrop-blur flex items-center px-2 sm:px-4 gap-1.5 sm:gap-3 shrink-0">
        <button onClick={onClose} className="p-2 rounded hover:bg-white/10 text-muted-strong" aria-label="Close">
          <X size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <FileName name={asset.filename} className="block text-sm font-medium text-white truncate" />
          <div className="text-[11px] text-muted truncate">{asset.kind.replace('_', ' ')} · {asset.content_type ?? 'unknown'}{navIndex >= 0 && <span data-testid="review-position"> · {navIndex + 1} of {navList.length}</span>}</div>
        </div>
        {navList.length > 0 && onNavigate && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => prevAsset && onNavigate(prevAsset)} disabled={!prevAsset} className="p-2 rounded hover:bg-white/10 text-muted-strong disabled:opacity-30" title="Previous file ([)" aria-label="Previous file" data-testid="review-prev"><ChevronLeft size={18} /></button>
            <button onClick={() => nextAsset && onNavigate(nextAsset)} disabled={!nextAsset} className="p-2 rounded hover:bg-white/10 text-muted-strong disabled:opacity-30" title="Next file (])" aria-label="Next file" data-testid="review-next"><ChevronRight size={18} /></button>
          </div>
        )}
        {/* The file in hand: save it or delete it without leaving the review. */}
        {canSave && (
          <button onClick={() => void downloadOne(asset)} disabled={!!saving} data-testid="review-download"
            className="inline-flex items-center gap-1.5 text-[11px] p-2 sm:px-2.5 sm:py-1.5 rounded bg-white/10 hover:bg-white/20 text-muted-strong border border-slate-700 transition disabled:opacity-60 shrink-0"
            title="Download the original (D)" aria-label="Download the original">
            {(() => {
              const job = liveJob(asset.id);
              const busy = saving?.id === asset.id || (job && job.state !== 'paused');
              const label = job ? (job.state === 'paused' ? 'Paused' : job.state === 'queued' ? 'Queued' : `${job.pct}%`)
                : saving?.id === asset.id ? (saving.pct != null ? `${saving.pct}%` : 'Saving...') : 'Download';
              return <>{busy ? <Loader2 size={13} className="animate-spin" /> : <DownloadIcon size={13} />}<span className="hidden sm:inline">{label}</span></>;
            })()}
          </button>
        )}
        {asset.client_approved && (
          <span className="shrink-0 text-[11px] font-semibold px-2 py-1 rounded bg-emerald-600 text-white" data-testid="review-clinic-approved"
                title="The clinic approved this file. It is locked: only the owner or a manager can remove it.">clinic approved</span>
        )}
        {mayDelete(asset) && (
          <button onClick={() => setPendingDel(asset)} data-testid="review-delete"
            className="inline-flex items-center gap-1.5 text-[11px] p-2 sm:px-2.5 sm:py-1.5 rounded bg-red-500/10 hover:bg-red-500/25 text-red-300 border border-red-500/40 transition shrink-0"
            title="Delete this file" aria-label="Delete this file">
            <Trash2 size={13} /> <span className="hidden sm:inline">Delete</span>
          </button>
        )}
        <span className="hidden sm:inline text-[11px] text-muted">
          {threads.length} thread{threads.length === 1 ? '' : 's'}
          {resolvedCount > 0 && <span className="text-emerald-400"> · {resolvedCount} resolved</span>}
        </span>

        {/* Export markers - opens dropdown with Premiere/DaVinci/CSV options.
            Hidden when there's nothing to export (no timestamped reviews). */}
        {threads.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setExportMenuOpen(v => !v)}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 text-[11px] p-2 sm:px-2.5 sm:py-1.5 rounded bg-white/10 hover:bg-white/20 text-muted-strong border border-slate-700 transition disabled:opacity-50"
              title="Download markers for editing software" aria-label="Export markers"
            >
              <MapPin size={12} /> <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export markers'}</span>
            </button>
            {exportMenuOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-64 rounded-lg bg-slate-900 ring-1 ring-slate-700 shadow-2xl z-40 overflow-hidden"
                onMouseLeave={() => setExportMenuOpen(false)}
              >
                <button onClick={() => exportMarkers('xmp')}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-800 transition border-b border-slate-800">
                  <div className="text-xs font-medium text-muted-strong">Premiere / After Effects</div>
                  <div className="text-[10px] text-muted">XMP sidecar - drop next to the video file</div>
                </button>
                <button onClick={() => exportMarkers('edl')}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-800 transition border-b border-slate-800">
                  <div className="text-xs font-medium text-muted-strong">DaVinci Resolve / FCP / Avid</div>
                  <div className="text-[10px] text-muted">EDL with marker entries</div>
                </button>
                <button onClick={() => exportMarkers('csv')}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-800 transition">
                  <div className="text-xs font-medium text-muted-strong">CSV (universal)</div>
                  <div className="text-[10px] text-muted">For spreadsheets or custom scripts</div>
                </button>
              </div>
            )}
          </div>
        )}
      </header>
      {/* Body: stage left, comments right */}
      <div className="flex-1 flex flex-col overflow-y-auto lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:overflow-hidden">
        {/* Stage */}
        <div className="relative bg-black flex flex-col items-center justify-center p-2 sm:p-4 min-h-0 min-w-0 overflow-hidden shrink-0 lg:shrink">
          <div ref={stageRef} className="relative max-h-full max-w-full" style={{ aspectRatio: 'auto' }}>
            {isVideo && (
              <video
                data-testid="review-video"
                ref={attachVideo}
                src={blobUrl ?? playable}
                controls={false}
                playsInline
                // Older iOS reads the legacy attribute, and React will not emit it
                // from a camelCase prop, so it is spread in as-is.
                {...{ 'webkit-playsinline': 'true', 'x5-playsinline': 'true' }}
                disablePictureInPicture
                preload="metadata"
                className={cn('block max-w-full',
                  isFull ? 'max-h-[calc(100dvh-330px)]' : 'max-h-[45vh] lg:max-h-[calc(100dvh-330px)]')}
                onClick={togglePlay}
              />
            )}
            {isImage && (
              <img
                data-testid="review-image"
                ref={imageRef}
                src={playable}
                alt={asset.filename}
                className={cn('block max-w-full',
                  isFull ? 'max-h-[calc(100dvh-330px)]' : 'max-h-[45vh] lg:max-h-[calc(100dvh-330px)]')}
              />
            )}
            {shown === 'preview-image' && !isImage && (
              <div className="text-center p-12" data-testid="still-preview-pending">
                <div className="text-sm text-white font-medium">The preview of this {typeBadge(asset)} photo is being made.</div>
                <div className="text-[12px] text-white/60 mt-1">It shows here in a minute. The original is ready to download meanwhile.</div>
                {(asset.attachment_url || asset.download_url) && (
                  <a className="inline-block mt-3 text-[12px] text-accent-400 hover:underline" href={asset.attachment_url || asset.download_url || ''}
                    onClick={(e) => { if (hasCenter) { e.preventDefault(); void downloadOne(asset); } }}>Download the original</a>
                )}
              </div>
            )}
            {!isVideo && !isImage && shown !== 'preview-image' && <FileViewer asset={asset} />}

            {/* Fetching the file so it can play from memory. Real bytes, a real
                percentage, and never a trap: streaming is one click away. */}
            {preloading && isVideo && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-black/70 backdrop-blur-sm"
                   data-testid="prebuffer">
                <Loader2 size={22} className="text-accent-400 animate-spin" />
                <div className="text-sm text-white font-medium" data-testid="prebuffer-pct">
                  {preloadPct}% ready{preloadEta ? ` - about ${preloadEta}` : ''}
                </div>
                <div className="w-56 max-w-[70%] h-1.5 rounded-full bg-white/15 overflow-hidden">
                  <div className="h-full bg-accent-400 transition-all duration-500"
                       style={{ width: `${preloadPct}%` }} />
                </div>
                <div className="text-[11px] text-white/60 max-w-[24rem] text-center px-4">
                  {formatBytes(gotBytes)} of {formatBytes(assetBytes)}
                  {preloadBps ? ` at ${formatBytes(preloadBps)}/s` : ''}. Camera master:
                  fetching it first means it plays without stalling.
                </div>
                <button onClick={playStreaming}
                  className="mt-1 text-[11px] text-accent-400 hover:underline" data-testid="play-anyway">
                  Play now anyway
                </button>
              </div>
            )}

            {/* One strip across the top for everything about the file itself:
                a proxy on its way (with how long), and a master that is too big
                to hold. Two corner notes overlapped on a phone. */}
            {masterUnplayable && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-black/85 text-center px-6"
                   data-testid="master-unplayable">
                <div className="text-sm text-white font-medium">This camera file does not play in this browser.</div>
                {proxyPending ? (
                  <div className="text-[12px] text-white/70 max-w-[26rem]" data-testid="web-copy-coming">
                    A web copy is being made, about {formatEta(etaLeft)}. It opens here by itself when it is ready.
                  </div>
                ) : (
                  <>
                    <div className="text-[12px] text-white/70 max-w-[26rem]">
                      A web copy plays everywhere, Safari and phones included. It takes a few minutes.
                    </div>
                    <button type="button" disabled={askingCopy} data-testid="make-web-copy"
                      className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-accent-500 hover:bg-accent-400 text-white disabled:opacity-60"
                      onClick={async () => {
                        setAskingCopy(true);
                        try { await requestWebCopy(asset.id); await qc.invalidateQueries({ queryKey: ['assets'] }); toast.success('Web copy requested'); }
                        catch { toast.error('Could not ask for a web copy'); }
                        finally { setAskingCopy(false); }
                      }}>
                      {askingCopy && <Loader2 size={12} className="animate-spin" />} Make a web copy
                    </button>
                  </>
                )}
                {asset.attachment_url && (
                  <a href={asset.attachment_url} className="text-[11px] text-accent-400 hover:underline mt-1"
                    onClick={(e) => { if (hasCenter) { e.preventDefault(); void downloadOne(asset); } }}>Download the original</a>
                )}
              </div>
            )}
            {isVideo && !hasProxy && !blobUrl && !masterUnplayable && (proxyPending || tooBigToHold) && (
              <div className="absolute top-2 left-2 right-2 z-10 text-[11px] leading-snug px-2.5 py-1.5 rounded-md bg-black/75 text-white/85 ring-1 ring-white/15 pointer-events-none backdrop-blur-sm"
                   data-testid={proxyPending ? 'proxy-pending' : 'stream-note'}>
                {proxyPending && (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-white">Preparing a streaming copy</span>
                      <span className="tabular-nums text-white/80" data-testid="proxy-eta">about {formatEta(etaLeft)}</span>
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-white/15 overflow-hidden">
                      <div className="h-full bg-accent-400 transition-all duration-1000" style={{ width: `${proxyPct}%` }}
                           data-testid="proxy-bar" />
                    </div>
                  </>
                )}
                {tooBigToHold && (
                  <div className={cn('text-white/65', proxyPending && 'mt-1')}>
                    {proxyPending ? 'Meanwhile the ' : ''}{formatBytes(assetBytes)} master {proxyPending ? 'plays; it' : 'is more than this device can hold, so it'} streams and may pause to load.
                  </div>
                )}
              </div>
            )}

            {/* "Pin" indicator: lets the user see when a mark is anchored to the current frame.
                Bottom corner, so it never shares the top strip. */}
            {isVideo && shapesShown.length > 0 && (
              <div className="absolute bottom-2 left-2 z-10 text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-accent-500 text-white shadow pointer-events-none">
                {activeReview ? `Viewing mark @ ${fmtTime(activeReview.timestamp_sec ?? 0)}` :
                  pinTime !== null && Math.abs(currentTime - pinTime) <= TIMESTAMP_TOLERANCE
                    ? `Drafting mark @ ${fmtTime(pinTime)}`
                    : `Mark visible at ${fmtTime(currentTime)}`}
              </div>
            )}

            {/* Drawing overlay */}
            <svg
              data-testid="markup-layer"
              ref={svgRef}
              className={cn(
                'absolute inset-0 w-full h-full',
                tool === 'select' ? 'cursor-default pointer-events-none' : 'cursor-crosshair',
              )}
              // touchAction:none stops mobile browsers from scrolling/zooming while drawing
              style={{ touchAction: tool === 'select' ? 'auto' : 'none' }}
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              onPointerDown={onSvgPointerDown}
              onPointerMove={onSvgPointerMove}
              onPointerUp={onSvgPointerUp}
              onPointerLeave={onSvgPointerUp}
            >
              {shapesShown.map((s, i) => renderShape(s, i))}
            </svg>
          </div>

          {/* Player controls */}
          {isVideo && (
            <div className="w-full max-w-3xl mt-3 flex items-center gap-3">
              <button onClick={togglePlay} data-testid="play-pause" aria-label={playing ? 'Pause' : 'Play'}
                title="Play/pause (Space or K). J/L = back/fwd 5s. Arrows = 1s. Shift+arrow/J/L = 1 frame."
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white">
                {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
              </button>

              {/* Volume cluster: mute toggle + slider that fades in on hover */}
              <div className="group flex items-center">
                <button
                  onClick={toggleMute}
                  title={muted ? 'Unmute (M)' : 'Mute (M)'}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
                >
                  {muted || volume === 0 ? <VolumeX size={16} /> : volume < 0.5 ? <Volume1 size={16} /> : <Volume2 size={16} />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={muted ? 0 : volume}
                  onChange={(e) => setVolume(parseFloat(e.target.value))}
                  title="Volume"
                  className="w-0 group-hover:w-20 ml-0 group-hover:ml-2 transition-all duration-200 accent-accent-500 opacity-0 group-hover:opacity-100"
                />
              </div>

              <span className="text-[11px] font-mono text-muted-strong tabular-nums w-14 text-right">{fmtTime(currentTime)}</span>
              <div
                ref={scrubberRef}
                className="flex-1 relative"
                onPointerMove={onScrubberHover}
                onPointerLeave={() => setScrubHover(null)}
              >
                <input
                  type="range"
                  min={0}
                  max={Math.max(0.01, duration)}
                  step={0.01}
                  value={currentTime}
                  onChange={(e) => seekTo(parseFloat(e.target.value))}
                  className="w-full accent-accent-500"
                />

                {/* Hover thumbnail preview: shows the right tile from the sprite using
                    background-position offset. Falls back to a clean timecode-only chip
                    when the sprite hasn't been generated yet. */}
                {scrubHover && (() => {
                  const cols = asset.preview_sprite_cols ?? 0;
                  const rows = asset.preview_sprite_rows ?? 0;
                  const count = asset.preview_sprite_count ?? 0;
                  const tileW = asset.preview_tile_w ?? 0;
                  const tileH = asset.preview_tile_h ?? 0;
                  const sprite = asset.preview_sprite_url;
                  const hasSprite = !!sprite && cols > 0 && rows > 0 && count > 0 && tileW > 0 && tileH > 0;

                  let tileStyle: React.CSSProperties = { width: 160, height: 90 };
                  if (hasSprite && duration > 0) {
                    const idx = Math.min(count - 1, Math.floor((scrubHover.time / duration) * count));
                    const col = idx % cols;
                    const row = Math.floor(idx / cols);
                    const previewW = 160;
                    const previewH = Math.round(tileH * (previewW / tileW));
                    tileStyle = {
                      width: previewW,
                      height: previewH,
                      backgroundImage: `url(${sprite})`,
                      backgroundSize: `${cols * previewW}px ${rows * previewH}px`,
                      backgroundPosition: `-${col * previewW}px -${row * previewH}px`,
                    };
                  }

                  return (
                    <div
                      className="absolute bottom-full mb-2 -translate-x-1/2 z-30 pointer-events-none"
                      style={{ left: `${scrubHover.pct * 100}%` }}
                    >
                      <div className="rounded-md overflow-hidden ring-1 ring-white/30 shadow-2xl bg-black flex items-center justify-center text-[10px] text-white/60"
                           style={tileStyle}>
                        {!hasSprite && 'Preview not generated yet'}
                      </div>
                      <div className="text-center mt-1 text-[10px] font-mono text-white bg-black/80 rounded px-1.5 py-0.5 inline-block left-1/2 -translate-x-1/2 relative">
                        {fmtTime(scrubHover.time)}
                      </div>
                    </div>
                  );
                })()}
                {/* Scrubber pins: cluster reviews that share a near-identical timestamp
                    (proximity = max(0.5s, 0.5% of duration). One stacked pin per cluster
                    with a count badge; hover tooltip lists every review in the cluster. */}
                {duration > 0 && clusterReviews((reviews.data ?? []).filter(r => !r.parent_id), duration).map((bucket) => {
                  const headTime = bucket[0].timestamp_sec ?? 0;
                  const pct = Math.max(0, Math.min(100, (headTime / duration) * 100));
                  const anyHasDrawing = bucket.some(r => (r.drawing?.length ?? 0) > 0);
                  const isCluster = bucket.length > 1;
                  return (
                    <div
                      key={bucket.map(r => r.id).join('|')}
                      className="group absolute -top-5 -translate-x-1/2 z-20 leading-none"
                      style={{ left: `${pct}%` }}
                    >
                      <button
                        onClick={() => {
                          seekTo(headTime);
                          if (!isCluster) setActiveReviewId(bucket[0].id);
                        }}
                        className="block relative"
                        title={isCluster ? `${bucket.length} reviews near ${fmtTime(headTime)}` : undefined}
                      >
                        <MapPin
                          size={isCluster ? 24 : 20}
                          fill={anyHasDrawing ? '#fbbf24' : '#38bdf8'}
                          strokeWidth={1.5}
                          className={cn(
                            'transition-transform group-hover:scale-125 drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]',
                            anyHasDrawing ? 'text-amber-700' : 'text-sky-700',
                          )}
                        />
                        {isCluster && (
                          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-slate-900 ring-1 ring-white text-white text-[9px] font-bold flex items-center justify-center">
                            {bucket.length}
                          </span>
                        )}
                      </button>
                      {/* Hover tooltip - shows every review in the cluster */}
                      <div className="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 w-64 rounded-lg bg-slate-900 ring-1 ring-slate-700 text-left text-[11px] text-white opacity-0 group-hover:opacity-100 transition shadow-xl z-30 overflow-hidden">
                        {bucket.map((r) => {
                          const hasDrawing = (r.drawing?.length ?? 0) > 0;
                          return (
                            <button
                              key={r.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                seekTo(r.timestamp_sec ?? 0);
                                setActiveReviewId(r.id);
                              }}
                              className="w-full text-left px-2.5 py-1.5 hover:bg-slate-800 border-b border-slate-800 last:border-0 transition"
                            >
                              <div className="font-medium text-accent-300 truncate">
                                {r.author_name}
                                <span className="text-muted ml-1.5 font-mono">@ {fmtTime(r.timestamp_sec ?? 0)}</span>
                                {hasDrawing && <span className="text-amber-300 ml-1.5">· {r.drawing!.length} mark{r.drawing!.length === 1 ? '' : 's'}</span>}
                              </div>
                              <div className="text-white/80 line-clamp-2 mt-0.5 whitespace-normal">{r.body}</div>
                            </button>
                          );
                        })}
                        {/* downward arrow */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 rotate-45 bg-slate-900 ring-1 ring-slate-700" />
                      </div>
                    </div>
                  );
                })}
              </div>
              <span className="text-[11px] font-mono text-muted tabular-nums w-14">{fmtTime(duration)}</span>
              {canFullscreen && (
                <button
                  onClick={toggleFullscreen}
                  title={isFull ? 'Leave fullscreen (F or Esc)' : 'Fullscreen, with the drawing tools (F)'}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white shrink-0"
                >
                  {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
              )}
            </div>
          )}

          {/* Editor-shortcut cheatsheet (desktop only - mobile users won't have a keyboard) */}
          {isVideo && (
            <div className="hidden md:block mt-1.5 text-[10px] text-muted/70 font-mono">
              space/K play · J/L = ±5s · ←/→ ±1s · Shift+ ±1 frame · M mute · F fullscreen · V/P/R/A/T tools{canSave ? ' · D download' : ''}
            </div>
          )}

          {!isVideo && canSave && (
            <div className="hidden md:block mt-1.5 text-[10px] text-muted/70 font-mono">D download · [ / ] previous / next file</div>
          )}

          {/* Tools palette */}
          {(isVideo || isImage) && (
            <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5 px-3 py-2 rounded-2xl lg:rounded-full bg-bg-card/80 backdrop-blur border border-slate-800 max-w-full">
              <ToolBtn label="Select (V)"    active={tool === 'select'} onClick={() => setTool('select')}><MousePointer2 size={14} /></ToolBtn>
              <ToolBtn label="Pencil (P)"    active={tool === 'pencil'} onClick={() => setTool('pencil')}><Pencil size={14} /></ToolBtn>
              <ToolBtn label="Rectangle (R)" active={tool === 'rect'}   onClick={() => setTool('rect')}><Square size={14} /></ToolBtn>
              <ToolBtn label="Arrow (A)"     active={tool === 'arrow'}  onClick={() => setTool('arrow')}><ArrowUpRight size={14} /></ToolBtn>
              <ToolBtn label="Text (T)"      active={tool === 'text'}   onClick={() => setTool('text')}><TextIcon size={14} /></ToolBtn>
              <span className="w-px h-5 bg-slate-700 mx-1" />
              {COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  title={c}
                  className={cn('w-5 h-5 rounded-full border-2 transition', color === c ? 'border-white scale-110' : 'border-transparent')}
                  style={{ backgroundColor: c }}
                />
              ))}
              <span className="w-px h-5 bg-slate-700 mx-1" />
              {([[20, 'S'], [28, 'M'], [40, 'L'], [56, 'XL']] as const).map(([px, lbl]) => (
                <button key={px} onClick={() => setTextSize(px)} title={`Text size ${lbl}`} data-testid="text-size"
                  className={cn('w-6 h-6 rounded text-[10px] font-semibold transition',
                    textSize === px ? 'bg-accent-600 text-white' : 'text-muted hover:text-muted-strong hover:bg-white/10')}>{lbl}</button>
              ))}
              <span className="w-px h-5 bg-slate-700 mx-1" />
              <ToolBtn label="Undo" onClick={undo} disabled={allMarks.length === 0}><Undo2 size={14} /></ToolBtn>
              <ToolBtn label="Clear" onClick={clearDrawing} disabled={allMarks.length === 0}><Trash2 size={14} /></ToolBtn>
            </div>
          )}

          {/* The other files in this view, under the player and right above the review box.
              On a phone the next take is then a thumb away from what you just wrote, rather
              than back at the top of a page you have to scroll the whole video to reach. */}
          {navList.length > 0 && onNavigate && (
            <div ref={stripRef} className="relative mt-3 w-full max-w-full min-w-0 shrink-0 rounded-2xl lg:rounded-xl border border-slate-800 bg-bg-card/60 flex items-center gap-1.5 px-3 py-2 overflow-x-auto" data-testid="review-strip">
              {navList.map((a, i) => {
                const on = a.id === asset.id;
                const vid = a.content_type?.startsWith('video/');
                const canDel = mayDelete(a);
                const canDl = !!(a.download_url || a.attachment_url);
                return (
                  <div key={a.id} className="group relative shrink-0">
                  <button type="button" onClick={() => !on && onNavigate(a)} title={a.filename} data-testid="review-strip-item" data-current={on ? 'true' : 'false'} data-compact
                    // Scroll the strip only. scrollIntoView also scrolled every box around it, so
                    // with 74 raws open at file 52 the whole player slid sideways and the video
                    // showed as a strip at the screen edge (Mahmoud, Safari, 2026-09-28).
                    ref={(el) => { const s = stripRef.current; const box = el?.parentElement; if (on && box && s) s.scrollLeft = box.offsetLeft - (s.clientWidth - box.offsetWidth) / 2; }}
                    className={cn('relative block w-16 h-11 rounded-md overflow-hidden border bg-slate-900 flex items-center justify-center', on ? 'border-accent-400 ring-1 ring-accent-400/50' : 'border-slate-800 opacity-70 hover:opacity-100')}>
                    {a.thumbnail_url ? <img src={a.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" /> : (vid ? <Film size={14} className="text-muted" /> : <ImageIcon size={14} className="text-muted" />)}
                    <span className="absolute bottom-0 left-0 right-0 text-[9px] leading-3 px-1 bg-black/60 text-white/90 truncate text-left">{i + 1}</span>
                  </button>
                  {/* hover only: a phone has no hover, and a tap must never land on delete;
                      there the buttons in the bar above do the same for the open file */}
                  {(canDl || canDel) && (
                    <div className="hidden [@media(hover:hover)]:group-hover:flex absolute top-0.5 right-0.5 gap-0.5" data-testid="strip-actions">
                      {canDl && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); void downloadOne(a); }} disabled={!!saving}
                          className="p-0.5 rounded bg-black/75 text-white hover:bg-accent-500" title={`Download ${a.filename}`} aria-label={`Download ${a.filename}`} data-testid="strip-download">
                          <DownloadIcon size={11} />
                        </button>
                      )}
                      {canDel && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setPendingDel(a); }}
                          className="p-0.5 rounded bg-black/75 text-red-300 hover:bg-red-600 hover:text-white" title={`Delete ${a.filename}`} aria-label={`Delete ${a.filename}`} data-testid="strip-delete">
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right rail: comments */}
        <aside className="border-t lg:border-t-0 lg:border-l border-slate-800 bg-bg-card/40 flex flex-col min-h-0 flex-1">
          <div className="p-3 border-b border-slate-800">
            <div className="flex items-start gap-2 mb-2">
              <MessageSquare size={14} className="text-accent-400 mt-1 shrink-0" />
              <div>
                <div className="text-sm font-medium text-muted-strong">Reviews on this asset</div>
                <div className="text-[10px] text-muted">{isClient
                  ? 'Draw on the frame with the pencil, rectangle or arrow, then describe the change. The team is notified.'
                  : 'Pencil/rectangle/arrow on the frame, then describe the change. Editor + AD + CC + uploader get pinged.'}</div>
              </div>
            </div>
          </div>

          {/* Composer */}
          <div className="p-3 border-b border-slate-800 bg-slate-900/40">
            {activeReview && (
              <div className="mb-2 px-2.5 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center gap-2 text-[11px] text-amber-300">
                <span className="flex-1">Viewing {activeReview.author_name}'s mark. Start typing or drawing to add your own.</span>
                <button
                  onClick={() => setActiveReviewId(null)}
                  className="text-muted-strong hover:text-white"
                  title="Close view"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            {pinTime !== null && isVideo && !activeReview && (
              <div className="text-[10px] text-accent-300 mb-1.5 flex items-center gap-1.5">
                <Star size={10} fill="currentColor" /> Pinned to {[...stash.map((m) => m.time), pinTime].sort((a, b) => a - b).map(fmtTime).join(', ')}
                {(() => { const n = draftShapes.length + stash.reduce((k, m) => k + m.shapes.length, 0); return n > 0 ? ` · ${n} mark${n === 1 ? '' : 's'}${stash.length ? ` on ${stash.length + 1} frames` : ''}` : ''; })()}
                <button onClick={clearDraft} className="ml-auto text-muted hover:text-muted-strong text-[10px] underline">unpin</button>
              </div>
            )}
            {allMarks.length > 0 && !activeReview && (
              <div className="mb-2 rounded-lg border border-slate-800 divide-y divide-slate-800/60" data-testid="mark-list">
                {allMarks.map((m) => (
                  <div key={`${m.frame}:${m.index}`} className="flex items-center gap-2 px-2 py-1 text-[10px]" data-testid="mark-row">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: (m.shape as { color: string }).color }} />
                    <span className="text-muted-strong truncate">{markLabel(m.shape)}</span>
                    {isVideo && m.time !== null && <span className="text-muted font-mono">{fmtTime(m.time)}</span>}
                    <button onClick={() => removeMark(m.frame, m.index)} data-testid="mark-remove"
                      className="ml-auto text-muted hover:text-red-300 shrink-0" title="Remove this mark" aria-label="Remove this mark">
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div onFocusCapture={() => {
              // Focusing the composer auto-exits "viewing existing review" mode. It no
              // longer pins the frame: the mark follows the playhead until a shape is drawn.
              if (activeReviewId) setActiveReviewId(null);
            }}>
              <MentionTextarea
                textareaRef={textareaRef}
                value={body}
                onChange={setBody}
                mentions={mentions}
                onMentionsChange={setMentions}
                placeholder={isVideo
                  ? (isClient ? 'What needs to change? (paused frame is captured)' : 'What needs to change? (paused frame is captured) - type @ to mention')
                  : (isClient ? 'What needs to change?' : 'What needs to change? - type @ to mention')}
                rows={3}
              />
              {mentions.length > 0 && (
                <div className="text-[10px] text-muted mt-1">{mentions.length} teammate{mentions.length === 1 ? '' : 's'} will be pinged</div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-2">
              <VoiceRecorder value={voice} onChange={setVoice} disabled={submitting} compact />
              <button
                onClick={handlePost}
                data-testid="review-post"
                disabled={submitting || !body.trim()}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-accent-500 hover:bg-accent-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium"
              >
                <Send size={12} /> {submitting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>

          {/* Filter pill - hide resolved threads */}
          {resolvedCount > 0 && (
            <div className="px-3 py-2 border-b border-slate-800 flex items-center gap-2">
              <button
                onClick={() => setHideResolved(v => !v)}
                className={cn(
                  'text-[11px] px-2 py-1 rounded-full border transition inline-flex items-center gap-1',
                  hideResolved
                    ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                    : 'border-slate-800 text-muted hover:text-muted-strong',
                )}
              >
                {hideResolved ? `Showing open only (${openCount})` : `Hide resolved (${resolvedCount})`}
              </button>
            </div>
          )}

          {/* Threads */}
          <div className="flex-1 lg:overflow-y-auto p-3 space-y-2.5">
            {reviews.isLoading && <div className="text-xs text-muted text-center py-4">Loading reviews...</div>}
            {!reviews.isLoading && visibleThreads.length === 0 && (
              <div className="text-xs text-muted text-center py-8">
                {threads.length === 0
                  ? <>No reviews yet.<br />Be the first to mark this up.</>
                  : 'All threads are resolved. Toggle off to see them.'}
              </div>
            )}
            {visibleThreads.map(({ parent, replies }) => (
              <ReviewThread
                key={parent.id}
                parent={parent}
                replies={replies}
                active={activeReviewId === parent.id}
                isVideo={!!isVideo}
                currentUserId={currentUser?.id ?? ''}
                isStaff={isStaff}
                onActivate={() => setActiveReviewId(activeReviewId === parent.id ? null : parent.id)}
                onSeek={seekTo}
                fmtTime={fmtTime}
                relTime={relTime}
              />
            ))}
          </div>
        </aside>
      </div>

      {/* Inside the player's own layer, so it sits above it (and stays in fullscreen). */}
      <ConfirmDialog
        open={!!pendingDel}
        title={pendingDel ? `Delete ${pendingDel.filename}?` : 'Delete file?'}
        message={<>This removes it for everyone. It can be recovered for 30 days.</>}
        confirmLabel="Delete"
        cancelLabel="Keep"
        tone="danger"
        onCancel={() => setPendingDel(null)}
        onConfirm={confirmDelete}
      />

      <PromptDialog
        open={textPrompt !== null}
        title="Add text to the frame"
        message="Enter starts a new line. Long lines wrap so the text stays on the picture."
        placeholder="Type your label..."
        confirmLabel="Add"
        multiline
        maxLength={600}
        onCancel={() => { setTextPrompt(null); setTool('select'); }}
        onConfirm={(text) => {
          const t = text.trim();
          if (t && textPrompt) {
            setDraftShapes((cur) => [...cur, { type: 'text', x: textPrompt.x, y: textPrompt.y, body: t, color, size: textSize }]);
          }
          setTextPrompt(null);
          // One label per press of the text tool, so the next touch pans or zooms
          // instead of opening this box again.
          setTool('select');
        }}
      />
    </div>
  );
}

// ---------- atoms ----------

/** Hand a URL to the browser as a download. Cross-origin signed URLs ignore the
 *  `download` name, which is why a named save goes through a Blob URL first. */
function clickLink(href: string, filename?: string) {
  const a = document.createElement('a');
  a.href = href;
  if (filename) a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function ToolBtn({ children, label, active, onClick, disabled }: { children: React.ReactNode; label: string; active?: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      data-testid="tool-btn"
      data-tool-active={active ? 'true' : 'false'}
      className={cn(
        // Bigger tap target on touch (~44px), tighter on desktop
        'p-2.5 sm:p-1.5 rounded transition disabled:opacity-30 disabled:cursor-not-allowed',
        active ? 'bg-accent-500 text-white' : 'text-muted-strong hover:bg-white/10',
      )}
    >
      {children}
    </button>
  );
}

// ---------- shape rendering ----------

// Rendered inside a viewBox="0 0 1 1" SVG with preserveAspectRatio="none".
// Stroke widths are in viewBox units (~ fraction of width). 0.006 => 0.6% of width,
// which is visible at any reasonable display size. We deliberately do NOT use
// vectorEffect="non-scaling-stroke" because that interprets strokeWidth as CSS px,
// turning sub-pixel values invisible.
function renderShape(s: DrawingShape, i: number) {
  const stroke = (s as { stroke?: number }).stroke ?? 0.006;
  if (s.type === 'rect') {
    return <rect key={i} x={s.x} y={s.y} width={s.w} height={s.h} fill="none" stroke={s.color} strokeWidth={stroke} />;
  }
  if (s.type === 'path') {
    return <path key={i} d={s.d} fill="none" stroke={s.color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" />;
  }
  if (s.type === 'ellipse') {
    return <ellipse key={i} cx={s.x + s.w / 2} cy={s.y + s.h / 2} rx={Math.abs(s.w / 2)} ry={Math.abs(s.h / 2)} fill="none" stroke={s.color} strokeWidth={stroke} />;
  }
  if (s.type === 'arrow') {
    const ang = Math.atan2(s.y2 - s.y1, s.x2 - s.x1);
    const headLen = 0.04;
    const hx1 = s.x2 - headLen * Math.cos(ang - Math.PI / 6);
    const hy1 = s.y2 - headLen * Math.sin(ang - Math.PI / 6);
    const hx2 = s.x2 - headLen * Math.cos(ang + Math.PI / 6);
    const hy2 = s.y2 - headLen * Math.sin(ang + Math.PI / 6);
    return (
      <g key={i} stroke={s.color} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeLinejoin="round">
        <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} />
        <line x1={s.x2} y1={s.y2} x2={hx1} y2={hy1} />
        <line x1={s.x2} y1={s.y2} x2={hx2} y2={hy2} />
      </g>
    );
  }
  if (s.type === 'text') {
    const size = (s.size ?? 28) / 1000;
    // Lines the author typed, then each one wrapped to what still fits on the frame
    // from where it was placed. Without this a long sentence ran off the picture
    // and out of the exported mark entirely.
    const lines = wrapText(s.body, s.x, size);
    return (
      <text
        key={i}
        x={s.x}
        y={s.y}
        fill={s.color}
        fontSize={size}
        fontFamily="ui-sans-serif, system-ui"
        style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.7)', strokeWidth: 0.003 }}
      >
        {lines.map((ln, k) => (
          <tspan key={k} x={s.x} dy={k === 0 ? 0 : size * 1.25}>{ln}</tspan>
        ))}
      </text>
    );
  }
  return null;
}

/** Break a label into lines that stay inside the frame.
 *
 *  The frame is 1 unit wide, so the room to the right of x is `1 - x`. A character is
 *  roughly 0.55 of the font size across in this face, which is close enough to keep
 *  text on the picture without measuring the DOM. A word longer than one line is cut
 *  rather than allowed to overhang. */
export function wrapText(body: string, x: number, size: number): string[] {
  const room = Math.max(0.12, 1 - x - 0.01);
  const perLine = Math.max(6, Math.floor(room / (size * 0.55)));
  const out: string[] = [];
  for (const para of (body ?? '').split(/\r?\n/)) {
    if (!para.trim()) { out.push(''); continue; }
    let line = '';
    for (const word of para.split(/\s+/)) {
      const piece = line ? `${line} ${word}` : word;
      if (piece.length <= perLine) { line = piece; continue; }
      if (line) out.push(line);
      // a single word that cannot fit is chopped at the edge
      let rest = word;
      while (rest.length > perLine) { out.push(rest.slice(0, perLine)); rest = rest.slice(perLine); }
      line = rest;
    }
    if (line) out.push(line);
  }
  return out.length ? out.slice(0, 12) : [''];
}

/** A one-line name for a mark, for the removable list beside the composer. */
function markLabel(s: DrawingShape): string {
  if (s.type === 'text') return `“${s.body.replace(/\s+/g, ' ').slice(0, 28)}”`;
  if (s.type === 'rect') return 'Box';
  if (s.type === 'ellipse') return 'Circle';
  if (s.type === 'arrow') return 'Arrow';
  return 'Drawing';
}

function ephemeralShape(d: { start: { x: number; y: number }; current: { x: number; y: number }; pathPoints?: { x: number; y: number }[] }, tool: Tool, color: string): DrawingShape | null {
  if (tool === 'rect') {
    const x = Math.min(d.start.x, d.current.x);
    const y = Math.min(d.start.y, d.current.y);
    const w = Math.abs(d.current.x - d.start.x);
    const h = Math.abs(d.current.y - d.start.y);
    return { type: 'rect', x, y, w, h, color, stroke: 0.006 };
  }
  if (tool === 'arrow') {
    return { type: 'arrow', x1: d.start.x, y1: d.start.y, x2: d.current.x, y2: d.current.y, color, stroke: 0.006 };
  }
  return null;
}

function pathFromPoints(pts: { x: number; y: number }[]): string {
  if (pts.length === 0) return '';
  return `M ${pts[0].x.toFixed(4)} ${pts[0].y.toFixed(4)} ` +
         pts.slice(1).map(p => `L ${p.x.toFixed(4)} ${p.y.toFixed(4)}`).join(' ');
}

// Group timestamped reviews into adjacency clusters so co-located pins don't overlap.
// Threshold: max(0.5s, 0.5% of total duration) — same logic Frame.io uses for sticker stacks.
function clusterReviews(reviews: AssetReview[], duration: number): AssetReview[][] {
  const proximitySec = Math.max(0.5, duration * 0.005);
  const sorted = reviews
    .filter(r => r.timestamp_sec != null)
    .slice()
    .sort((a, b) => (a.timestamp_sec ?? 0) - (b.timestamp_sec ?? 0));
  const clusters: AssetReview[][] = [];
  for (const r of sorted) {
    const last = clusters[clusters.length - 1];
    if (last && Math.abs((r.timestamp_sec ?? 0) - (last[0].timestamp_sec ?? 0)) <= proximitySec) {
      last.push(r);
    } else {
      clusters.push([r]);
    }
  }
  return clusters;
}

function fmtTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function relTime(iso: string): string {
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60)    return `${sec}s ago`;
  if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}
